import { jwtDecode } from 'jwt-decode';

const TOKEN_KEY = 'agendario_jwt_token';
const NAME_OVERRIDE_KEY = 'agendario_user_name_override';
const LOGIN_URL = import.meta.env.VITE_LOGIN_URL || 'http://localhost:5000';

// Ativa logs diagnósticos de auth apenas em DEV e com flag explícita.
// Para habilitar: VITE_AUTH_DEBUG=true no .env.local + npm run dev
const AUTH_DEBUG = import.meta.env.DEV && import.meta.env.VITE_AUTH_DEBUG === 'true';

export const authService = {
  /**
   * Salva o token JWT no localStorage
   */
  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
    // Propaga ao logger central (console-logger.js) para erros autenticados levarem identidade.
    if (typeof window !== 'undefined' && window.AgndLogger) window.AgndLogger.setToken(token);
  },

  /**
   * Obtém o token JWT do localStorage
   */
  getToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (AUTH_DEBUG) {
      console.debug('[authService.getToken] Buscando token em localStorage[' + TOKEN_KEY + ']:', token ? 'ENCONTRADO' : 'NÃO ENCONTRADO');
    }
    return token;
  },

  /**
   * Remove o token JWT do localStorage
   */
  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_OVERRIDE_KEY);
    if (typeof window !== 'undefined' && window.AgndLogger) window.AgndLogger.setToken(null);
  },

  /**
   * Verifica se o token é válido (não expirado)
   */
  isTokenValid() {
    const token = this.getToken();
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Token expira em menos de 5 minutos? Consideramos inválido
      if (decoded.exp < currentTime + 300) {
        return false;
      }

      return decoded.exp > currentTime;
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return false;
    }
  },

  /**
   * Obtém informações do usuário decodificando o token.
   * Se o usuário editou o nome manualmente, usa o override do localStorage.
   */
  getUserInfo() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode(token);
      const nameOverride = localStorage.getItem(NAME_OVERRIDE_KEY);
      return {
        userId: decoded.userId || decoded.sub,
        email: decoded.email,
        name: nameOverride || decoded.name,
        theme: decoded.theme ? parseInt(decoded.theme) : null,
        isNewUser: decoded.isNewUser === 'true' || decoded.isNewUser === true,
        exp: decoded.exp
      };
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  },

  /**
   * Persiste um nome customizado que sobrescreve o claim do JWT.
   * Chamado após o usuário editar o nome com sucesso.
   * @param {string} name
   */
  setLocalName(name) {
    localStorage.setItem(NAME_OVERRIDE_KEY, name);
  },

  /**
   * Remove o override de nome (ex: ao fazer logout ou trocar conta).
   */
  clearLocalName() {
    localStorage.removeItem(NAME_OVERRIDE_KEY);
  },

  /**
   * Obtém o tema do usuário do JWT
   * @returns {number|null} Valor do enum ThemeType ou null se não definido
   */
  getThemeFromToken() {
    const userInfo = this.getUserInfo();
    return userInfo?.theme || null;
  },

  /**
   * Verifica se é um novo usuário
   * @returns {boolean}
   */
  isNewUser() {
    const userInfo = this.getUserInfo();
    return userInfo?.isNewUser === true;
  },

  /**
   * Redireciona para a página de login
   */
  redirectToLogin() {
    this.removeToken();
    // Usar a origem atual (localhost ou domínio hospedado)
    const currentUrl = window.location.origin;
    const loginUrlWithReturn = `${LOGIN_URL}?returnUrl=${encodeURIComponent(currentUrl)}`;
    if (AUTH_DEBUG) {
      console.debug('[authService.redirectToLogin] Redirecionando para:', loginUrlWithReturn);
    }
    window.location.href = loginUrlWithReturn;
  },

  /**
   * Processa token recebido via query string após login
   */
  processLoginCallback() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (AUTH_DEBUG) {
      console.debug('[authService] processLoginCallback - token na URL?', token ? 'SIM' : 'NÃO');
    }

    if (token) {
      if (AUTH_DEBUG) {
        console.debug('[authService] Salvando token no localStorage...');
      }
      this.setToken(token);
      if (AUTH_DEBUG) {
        console.debug('[authService] Token salvo. Verificando...');
        const saved = localStorage.getItem(TOKEN_KEY);
        console.debug('[authService] Token recuperado do localStorage?', saved ? 'SIM' : 'NÃO');
      }
      // Remove token da URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    }

    if (AUTH_DEBUG) {
      console.debug('[authService] Nenhum token na URL');
    }
    return false;
  },

  /**
   * Verifica autenticação e redireciona se necessário
   */
  checkAuth() {
    if (AUTH_DEBUG) {
      console.debug('[authService.checkAuth] Iniciando verificação de autenticação...');
    }
    // Primeiro verifica se há token no query string
    const hasNewToken = this.processLoginCallback();
    if (AUTH_DEBUG) {
      console.debug('[authService.checkAuth] Resultado de processLoginCallback:', hasNewToken);
    }

    if (hasNewToken) {
      if (AUTH_DEBUG) {
        console.debug('[authService.checkAuth] Token novo processado, autenticado!');
      }
      return true;
    }

    // Verifica se o token atual é válido
    if (!this.isTokenValid()) {
      if (AUTH_DEBUG) {
        console.debug('[authService.checkAuth] Token não válido, redirecionando para login');
      }
      this.redirectToLogin();
      return false;
    }

    if (AUTH_DEBUG) {
      console.debug('[authService.checkAuth] Token válido');
    }
    return true;
  }
};
